import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Chevron } from "../components/status/atoms";
import { api, type EngineInfo, type PasskeySummary } from "../lib/api";

function when(ms: number | null): string {
  if (!ms) return "never";
  return new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function PasskeysSection() {
  const [keys, setKeys] = useState<PasskeySummary[]>([]);
  const [available, setAvailable] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [origin, setOrigin] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = () =>
    api
      .passkeys()
      .then((r) => {
        setKeys(r.passkeys);
        setAvailable(r.available);
        setEnabled(r.enabled);
        setOrigin(r.origin);
      })
      .catch(() => undefined);

  useEffect(() => {
    void load();
  }, []);

  const add = async () => {
    setBusy(true);
    setMsg("");
    try {
      const r = await api.addPasskey();
      setKeys(r.passkeys);
      setMsg("Passkey added. Signing in from outside now needs Face ID too.");
    } catch (e) {
      const name = e instanceof Error ? e.name : "";
      setMsg(
        name === "NotAllowedError" || name === "AbortError"
          ? "Cancelled."
          : name === "InvalidStateError"
            ? "This device already has a passkey here."
            : `Could not add a passkey: ${e instanceof Error ? e.message : String(e)}`,
      );
    } finally {
      setBusy(false);
    }
  };

  const remove = async (k: PasskeySummary) => {
    const last = keys.length === 1;
    if (
      !window.confirm(
        last
          ? `Remove "${k.name}"? It is the only passkey, so outside sign-in falls back to password only until you add another.`
          : `Remove "${k.name}"?`,
      )
    )
      return;
    setBusy(true);
    try {
      const r = await api.removePasskey(k.id);
      setKeys(r.passkeys);
      setMsg("");
    } catch (e) {
      setMsg(`Could not remove it: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  if (!enabled) return null;

  return (
    <section className="card section">
      <div className="section__head">
        <h2>Passkeys</h2>
        {available && (
          <button type="button" className="btn btn--ghost" onClick={() => void add()} disabled={busy}>
            {busy ? "Waiting…" : "Add passkey"}
          </button>
        )}
      </div>
      {keys.map((k) => (
        <div key={k.id} className="section__row">
          <span className="dot dot--ok" />
          <div className="grow">
            <strong>{k.name}</strong>
            <div className="section__sub">
              Added {when(k.createdAt)} · last used {when(k.lastUsed)}
              {k.backedUp ? " · synced" : ""}
            </div>
          </div>
          <button type="button" className="btn" onClick={() => void remove(k)} disabled={busy}>
            Remove
          </button>
        </div>
      ))}
      {keys.length === 0 && (
        <div className="hint">
          {available
            ? "No passkey yet, so signing in from outside is password only. Add one with Face ID — after that, outside sign-ins need both."
            : `Passkeys can only be added from ${origin || "the public address"}. On the home network the console stays password only.`}
        </div>
      )}
      {msg && <div className="hint">{msg}</div>}
    </section>
  );
}

export function SettingsView() {
  const [engines, setEngines] = useState<EngineInfo[]>([]);
  const [checking, setChecking] = useState(false);

  const refresh = (force = false) => {
    setChecking(true);
    api
      .engines(force)
      .then(({ engines: list }) => setEngines(list))
      .catch(() => undefined)
      .finally(() => setChecking(false));
  };

  useEffect(() => refresh(false), []);

  return (
    <div className="page">
      <header className="topbar">
        <h1 className="topbar__title">Settings</h1>
      </header>

      <div className="page-body">
        <div className="page-inner stack">
          <section className="card section">
            <div className="section__head">
              <h2>Chat engines</h2>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => refresh(true)}
                disabled={checking}
              >
                {checking ? "Checking…" : "Re-check"}
              </button>
            </div>
            {engines.map((e) => (
              <div key={e.id} className="section__row">
                <span className={`dot ${e.available ? "dot--ok" : "dot--fault"}`} />
                <div className="grow">
                  <strong>{e.label}</strong>
                  <div className="section__sub">{e.detail}</div>
                </div>
                <span className="pill mono">{e.defaultModel}</span>
              </div>
            ))}
            {!engines.some((e) => e.id === "claude" && e.available) && (
              <div className="hint">
                Claude needs a one-time browser login. Run{" "}
                <code className="md__inline">claude</code> once in a terminal on this
                machine, finish the login, then hit Re-check.
              </div>
            )}
          </section>

          <section className="card section">
            <div className="section__head">
              <h2>Access</h2>
            </div>
            <div className="section__row">
              <div className="grow">
                <strong>Signed in</strong>
                <div className="section__sub">
                  Sessions last 30 days and stay active while you use the console.
                </div>
              </div>
              <button type="button" className="btn" onClick={() => void api.logout()}>
                Sign out
              </button>
            </div>
          </section>

          <PasskeysSection />

          {/*
            The only entry point to /debug from inside the app. It has no tab
            and no sidebar item on purpose — it is an instrument, not a screen
            you browse to — but it has to be reachable from the home-screen web
            app, where there is no address bar to type a path into.
          */}
          <section className="card section">
            <div className="section__head">
              <h2>Diagnostics</h2>
            </div>
            <Link className="section__row section__row--go" to="/debug">
              <div className="grow">
                <strong>Viewport debug</strong>
                <div className="section__sub">
                  What iOS actually reports — safe-area insets, svh/dvh/lvh, and the
                  live shell measurements. Read this before changing the layout.
                </div>
              </div>
              <Chevron />
            </Link>
          </section>
        </div>
      </div>
    </div>
  );
}
